import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { S25EditableCustomAttributeComponent } from "../s25-editable/s25.editable-custom-attribute/s25.editable-custom-attribute.component";
import { Item } from "../../pojo/Item";
import { Bind } from "../../decorators/bind.decorator";
import { S25Util } from "../../util/s25-util";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { AttachmentService, FileItem } from "../../services/attachment.service";
import { appendImageSrcCacheBust, buildObjectFilestorePoolRef, FileStoreUtil } from "../../util/filestore.util";
import { maybeDeleteOldFileAfterOrgCaReplace } from "../../util/org.filestore.ref.util";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { Attribute } from "../s25-event/EventMicroI";
import type { S25WsMicroService } from "../../services/MicroServices/S25WsMicroService";
import { StudentEngagementUtil } from "./student.engagement.util";
import { FilestoreUploadCommitPlan } from "../../util/filestore-upload-commit.util";
import { CustomAttributeValueUpdateService } from "../../services/custom.attribute.value.update.service";
import { OrganizationProfileApi } from "../../services/organization/organization.profile.api";
import { Proto } from "../../pojo/Proto";

@TypeManagerDecorator("se-editable-logo")
@Component({
    selector: "se-editable-logo",
    template: ` @if (imageUrl()) {
            <img [src]="imageUrl()" alt="Object Image" class="object-image" (error)="imageUrl.set(undefined)" />
        } @else {
            <div
                class="image-placeholder"
                [class]="{
                    event: itemTypeId() === itemIds.Event,
                    org: itemTypeId() === itemIds.Organization,
                    contact: itemTypeId() === itemIds.Contact,
                }"
            >
                <s25-ng-icon [type]="iconProp()" [size]="80" [ariaLabel]="false" />
            </div>
        }
        @if (canEdit() && !showImageForm()) {
            <div class="edit-badge">
                <s25-ng-icon [type]="'edit'" [size]="16" [ariaLabel]="'Edit logo'" />
            </div>
        }
        @if (showImageForm()) {
            <div class="image-form-container">
                <div class="image-form">
                    <div class="form-header">
                        <h4>{{ header() }}</h4>
                        <s25-ng-icon-button
                            [type]="'close'"
                            [size]="20"
                            [ariaLabel]="'Close logo edit'"
                            (clicked)="cancelLogoEdit(); $event.stopPropagation()"
                        ></s25-ng-icon-button>
                    </div>

                    <div class="form-content">
                        @if (itemTypeId() === itemIds.Organization && poolFiles().length) {
                            <div class="pool-picker">
                                <label for="se-logo-pool">Use existing organization image (PNG, JPEG, etc.)</label>
                                <select id="se-logo-pool" class="cn-form__control" (change)="onPoolFilePick($event)">
                                    <option value="">— Select —</option>
                                    @for (f of poolFiles(); track f.itemName) {
                                        <option [value]="f.itemName">{{ f.itemName }}</option>
                                    }
                                </select>
                            </div>
                        }
                        <s25-ng-editable-custom-attribute
                            [attributeId]="logoAttrId()"
                            [attributeType]="urlAttrType"
                            [value]="imageAttrValue()"
                            [hasUploadFile]="true"
                            [deferFilestoreUntilCommit]="true"
                            [objectId]="itemId()"
                            [itemTypeId]="itemTypeId()"
                            [readOnly]="false"
                            (valChange)="urlInput.set($event)"
                        ></s25-ng-editable-custom-attribute>
                    </div>
                    @if (imageErrorMsg()) {
                        <p class="ngRed">{{ imageErrorMsg() }}</p>
                    }
                    <div class="form-actions">
                        <button
                            type="button"
                            class="btn btn-secondary"
                            (click)="cancelLogoEdit(); $event.stopPropagation()"
                        >
                            Cancel
                        </button>
                        <s25-ng-button [type]="'primary'" [onClick]="updateLogo" (clicked)="$event.stopPropagation()"
                            >Save</s25-ng-button
                        >
                        @if (imageUrl()) {
                            <s25-ng-button [type]="'danger'" [onClick]="deleteLogo" (clicked)="$event.stopPropagation()"
                                >Delete Logo</s25-ng-button
                            >
                        }
                    </div>
                </div>
            </div>
        }`,
    host: {
        "(click)": "canEdit() && showImageForm.set(true)",
        "[class.editing]": "showImageForm()",
        "[class.editable]": "canEdit()",
    },
    styles: `
        :host {
            position: relative;
        }

        .image-wrapper {
            position: relative;
            display: inline-block;
        }

        :host.editable {
            cursor: pointer;
        }

        :host.editable:hover::after {
            content: "";
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: inherit;
            z-index: 10;
        }

        :host.editable:hover::before {
            content: "Edit";
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 600;
            font-size: 1rem;
            z-index: 11;
            pointer-events: none;
        }

        :host.editing {
            cursor: default;
        }

        :host.editing:hover::after,
        :host.editing:hover::before {
            display: none;
        }

        .object-image {
            max-width: 300px;
            width: 100%;
            height: auto;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .object-image.profile-image {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            object-fit: cover;
            border: 3px solid #f0f0f0;
        }

        .image-placeholder {
            width: 300px;
            height: 200px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

            &.event {
                background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                border: 3px dashed #90caf9;
            }

            &.org {
                background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                border: 2px dashed #c3cfe2;
            }

            &.contact {
                background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                border: 2px dashed #c3cfe2;
            }
        }

        ::ng-deep .image-placeholder.org svg {
            color: #667eea;
            opacity: 0.7;
        }

        ::ng-deep .image-placeholder.event svg {
            color: #2196f3;
            opacity: 0.7;
        }

        .image-form-container {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            border-radius: 12px;
            width: 23em;
        }

        .image-form {
            background: white;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
            width: 400px;
            max-width: 90vw;
            max-height: 80vh;
            overflow-y: auto;
            position: relative;
        }

        .form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem;
            border-bottom: 1px solid #e0e0e0;
        }

        .form-header h4 {
            margin: 0;
            font-size: 1.125rem;
            font-weight: 600;
            color: #333;
        }

        .form-content {
            padding: 0.5rem 1.25rem;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: #555;
            font-size: 0.875rem;
        }

        .form-divider span {
            background: white;
            padding: 0 1rem;
            position: relative;
            color: #999;
            font-size: 0.875rem;
        }

        .image-preview img {
            max-width: 100%;
            max-height: 200px;
            border-radius: 6px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .form-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem;
            border-top: 1px solid #e0e0e0;
        }

        .pool-picker {
            margin-bottom: 1rem;
        }
        .pool-picker label {
            display: block;
            margin-bottom: 0.35rem;
            font-weight: 500;
        }

        .btn {
            padding: 0.5rem 1.25rem;
            border: none;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
        }

        .btn-secondary {
            background: #f5f5f5;
            color: #666;
        }

        .btn-secondary:hover {
            background: #e0e0e0;
        }

        ::ng-deep .image-form-container s25-ng-editable-custom-attribute label[for="customAttributeValue"] {
            display: none;
        }

        ::ng-deep .image-form-container s25-ng-editable-custom-attribute .file-wrapper {
            display: block;
        }

        @media (max-width: 768px) {
            .object-image {
                max-width: 200px;
            }

            .image-placeholder {
                width: 200px;
                height: 140px;
            }

            :host ::ng-deep .image-placeholder svg {
                width: 50px !important;
                height: 50px !important;
            }

            .image-form-container {
                position: fixed;
                inset: 0;
                width: 100%;
                height: 100%;
                border-radius: 0;
                padding: 1rem;
                box-sizing: border-box;
            }

            .image-form {
                width: 100%;
                max-width: 100%;
                max-height: calc(100vh - 2rem);
            }

            .form-header {
                padding: 1rem;
            }

            .form-header h4 {
                font-size: 1rem;
            }

            .form-content {
                padding: 0.5rem 1rem;
            }

            .form-actions {
                padding: 1rem;
                flex-wrap: wrap;
                gap: 0.5rem;
            }

            .form-actions .btn,
            .form-actions s25-ng-button {
                flex: 1 1 auto;
                min-width: 80px;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25IconButtonComponent, S25ButtonComponent, S25EditableModule, S25IconComponent],
})
export class SeEditableLogo {
    itemId = input.required<number>();
    itemTypeId = input.required<Item.Ids>();
    imageAttrValue = input(undefined, {
        alias: "imageAttr",
        transform: (attribute: Attribute | S25WsMicroService.ItemAttribute | string) => {
            if (typeof attribute === "string") return attribute;
            return attribute?.value;
        },
    });
    canEdit = input<boolean>();

    logoAttrId = computed(() => StudentEngagementUtil.LogoAttrIds[this.itemTypeId()]);
    protected iconProp = computed(() => (this.itemTypeId() === Item.Ids.Event ? "calendarDate" : "starFilled"));
    protected header = computed(() => `Update ${this.itemTypeId() === Item.Ids.Event ? "Event" : "Organization"} Logo`);
    protected itemIds = Item.Ids;
    urlAttrType = CustomAttributes.type.URL;
    attrComp = viewChild(S25EditableCustomAttributeComponent);

    showImageForm = signal(false);
    imageUrl = linkedSignal<string | null | undefined>(() => StudentEngagementUtil.parseUrl(this.imageAttrValue()));
    imageErrorMsg = signal<string>("");

    urlInput = signal("");

    /** Organization file pool (for logo picker). */
    poolFiles = signal<FileItem[]>([]);

    constructor() {
        effect(() => {
            if (this.showImageForm() && this.itemTypeId() === Item.Ids.Organization) {
                void this.loadOrgPoolFiles();
            }
        });
    }

    private async loadOrgPoolFiles(): Promise<void> {
        const [list, err] = await S25Util.Maybe(
            AttachmentService.getFileList(AttachmentService.ObjectType.ORGANIZATION, this.itemId()),
        );
        if (!err) {
            this.poolFiles.set((list ?? []).filter((f) => FileStoreUtil.isImageFileName(f.itemName)));
        }
    }

    onPoolFilePick(ev: Event): void {
        const name = (ev.target as HTMLSelectElement).value;
        if (!name) {
            return;
        }
        this.urlInput.set(buildObjectFilestorePoolRef(Item.Ids.Organization, this.itemId(), name));
        this.attrComp()?.onRefresh(this.imageAttrValue() ?? null);
    }

    @Bind
    cancelLogoEdit(): void {
        this.urlInput.set("");
        this.imageErrorMsg.set("");
        this.attrComp()?.onRefresh(this.imageAttrValue() ?? "");
        this.showImageForm.set(false);
    }

    @Bind
    async updateLogo() {
        const attrComp = this.attrComp();
        if (!attrComp) return;
        let value: string | void;
        const stagedFile = attrComp.stagedUpload.file();
        if (stagedFile?.fileRef) {
            value = await this.processFile(stagedFile, attrComp.stagedUpload.commitPlan());
        } else value = this.urlInput() ?? "";
        if (value) {
            const [_, error] = await S25Util.Maybe(this.persistLogoAttribute("mod", value));
            if (error) {
                this.imageErrorMsg.set(S25Util.errorText(error, ""));
                return console.error(error);
            }
            this.imageErrorMsg.set("");
            const displayUrl = StudentEngagementUtil.parseUrl(value) ?? "";
            this.imageUrl.set(appendImageSrcCacheBust(displayUrl));
            attrComp.onRefresh(value);
            this.showImageForm.set(false);
        }
    }

    async processFile(file: S25File, commitPlan: FilestoreUploadCommitPlan) {
        if (commitPlan.kind === "none") {
            return;
        }
        if (this.itemTypeId() === Item.Ids.Organization) {
            await maybeDeleteOldFileAfterOrgCaReplace(this.itemId(), this.imageAttrValue() as string | undefined);
        }
        await this.applyStagedUploadCommitPlan(commitPlan, file.name);
        const [_, error] = await S25Util.Maybe(
            AttachmentService.postFile(this.itemTypeId(), this.itemId(), file.fileRef, file.name, this.logoAttrId()),
        );
        if (error) {
            this.imageErrorMsg.set(S25Util.errorText(error, ""));
            return console.error(error);
        }
        this.imageErrorMsg.set("");
        return `object_type=${this.itemTypeId()}&object_id=${this.itemId()}&atrb_id=${this.logoAttrId()}&file_name=${file.name}`;
    }

    private async applyStagedUploadCommitPlan(
        commitPlan: FilestoreUploadCommitPlan,
        uploadFileName: string | undefined,
    ): Promise<void> {
        switch (commitPlan.kind) {
            case "none":
                return;
            case "replace":
                await AttachmentService.delFile(this.itemTypeId(), this.itemId(), commitPlan.deleteAttachmentName);
                if (this.itemTypeId() !== Item.Ids.Organization) {
                    await this.deletePriorBoundFileIfReplacing(uploadFileName);
                }
                return;
            case "rename":
            case "unique":
                if (this.itemTypeId() !== Item.Ids.Organization) {
                    await this.deletePriorBoundFileIfReplacing(uploadFileName);
                }
                return;
            default:
                return;
        }
    }

    private async deletePriorBoundFileIfReplacing(newFileName: string | undefined): Promise<void> {
        const currentValue = this.imageAttrValue();
        if (typeof currentValue !== "string" || !FileStoreUtil.isFileStoreUrl(currentValue)) {
            return;
        }
        const params = FileStoreUtil.parseFileStoreUrl(currentValue);
        if (!params?.fileName) {
            return;
        }
        const sanitizedNew = AttachmentService.sanitizeFilenameSpaces(newFileName) ?? newFileName;
        const sanitizedOld = AttachmentService.sanitizeFilenameSpaces(params.fileName) ?? params.fileName;
        if (sanitizedNew === sanitizedOld) {
            return;
        }
        await AttachmentService.delFile(this.itemTypeId(), this.itemId(), params.fileName, params.fileKey);
    }

    @Bind
    async deleteLogo() {
        if (this.itemTypeId() === Item.Ids.Organization) {
            await maybeDeleteOldFileAfterOrgCaReplace(this.itemId(), this.imageAttrValue() as string | undefined);
        }
        const [_, error] = await S25Util.Maybe(this.persistLogoAttribute("del"));
        if (error) {
            this.imageErrorMsg.set(S25Util.errorText(error, ""));
            return console.error(error);
        }
        this.imageErrorMsg.set("");
        this.imageUrl.set("");
        this.showImageForm.set(false);
        this.attrComp()?.onRefresh("");
    }

    private persistLogoAttribute(status: Proto.ServiceStatus, value?: string) {
        const attributeId = this.logoAttrId();
        if (attributeId == null) {
            return Promise.reject(new Error("Logo attribute is not configured for this item type."));
        }
        if (this.itemTypeId() === Item.Ids.Organization) {
            const logoValue = status === "del" ? null : value;
            return OrganizationProfileApi.saveProfile(this.itemId(), { updateLogo: true, logo: logoValue });
        }
        return CustomAttributeValueUpdateService.saveAttributeValue({
            itemTypeId: this.itemTypeId(),
            itemId: this.itemId(),
            attributeId,
            attributeType: this.urlAttrType,
            status,
            value,
        });
    }
}
